import { addDays, format, isAfter, isBefore, isSameDay } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { useToday } from 'shared/contexts/today';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { PlannedOccurrence } from 'shared/types/habit-schedule';
import { Task, TaskCheckOptions } from 'shared/types/task';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';
import { getTaskCheckOption } from 'shared/utils/get-task-check-option';
import { getTaskDayCompletionPercentage } from 'shared/utils/get-task-day-completion-percentage';

import { useData } from '../../use-data';
import { TimeButton } from './time-button';
import * as Styled from './upcoming-week.style';

export type WeekDayProps = {
  task: Task;
  date: Timestamp;
  entry?: PlannedOccurrence;
  onAddEntry: (date: Timestamp) => void;
  onChangeEntry: (entry: PlannedOccurrence) => void;
  onDeleteEntry: (date: Timestamp) => void;
  onComplete: (date: Timestamp) => void;
};

export const WeekDay: React.FC<WeekDayProps> = ({
  task,
  date,
  entry,
  onAddEntry,
  onChangeEntry,
  onDeleteEntry,
  onComplete,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn } = useData();

  const isToday = useMemo(() => isSameDay(date, today), [date, today]);

  const label = useMemo(() => {
    if (isToday) {
      return t('general.labels.dates.today');
    }
    if (isSameDay(date, addDays(today, 1))) {
      return t('general.labels.dates.tomorrow');
    }

    return format(date, 'EEEE');
  }, [date, isToday, t, today]);

  const checked = useMemo(
    () =>
      getTaskCheckOption(task, {
        day: date,
        today,
        weekStartsOn,
        showRepeatingAsTodo: true,
      }),
    [date, task, today, weekStartsOn],
  );

  const completionPercentage = useMemo(
    () =>
      task?.schedules?.length
        ? getTaskDayCompletionPercentage(task, {
            day: date,
            weekStartsOn,
          })
        : undefined,
    [date, task, weekStartsOn],
  );

  const isFuture = useMemo(() => isAfter(date, today), [date, today]);

  const overdue = useMemo(
    () =>
      !!entry &&
      checked === TaskCheckOptions.Unchecked &&
      isBefore(date, today) &&
      !isSameDay(date, today),
    [checked, date, entry, today],
  );

  const activeSchedule = useActiveSchedule(task.schedules);
  const target = useMemo(() => {
    if (!activeSchedule) {
      return;
    }

    return activeSchedule.frequency
      ? getScheduleFrequencyDayTarget(activeSchedule)
      : activeSchedule.completionTarget.count;
  }, [activeSchedule]);

  const _onComplete = useCallback(() => {
    if (
      task.schedules?.length &&
      !isSameDay(date, today) &&
      isAfter(date, today)
    ) {
      // do not allow completing habits for future dates
      return;
    }
    onComplete(date);
  }, [onComplete, date, task.schedules?.length, today]);

  const _onAdd = !entry ? () => onAddEntry(date) : undefined;
  const _onDelete = () => onDeleteEntry(date);

  const onChangeTimeslot = (timeslot: Timeslot | null) => {
    if (!entry) {
      return;
    }

    onChangeEntry({
      ...entry,
      timeslot,
    });
  };

  return (
    <Styled.WeekDayContainer as={!!entry ? 'div' : undefined} onClick={_onAdd}>
      {!!entry ? (
        !isFuture ? (
          <TaskDetailCheckbox
            checked={checked}
            name={task.name}
            type={task.type}
            completionPercentage={completionPercentage}
            target={target}
            priority={task?.priority}
            onChange={_onComplete}
            isFrozen={!!task?.frozenAt}
            icon={Calendar}
          />
        ) : (
          <Styled.StartIconContainer>
            <Icon icon={Calendar} />
          </Styled.StartIconContainer>
        )
      ) : (
        <Styled.StartIconContainer>
          <Icon icon={Plus} />
        </Styled.StartIconContainer>
      )}
      <Styled.LabelContainer
        $overdue={overdue}
        $hasEntry={!!entry}
        $isToday={isToday}
      >
        {label}
      </Styled.LabelContainer>

      {!!entry && (
        <Styled.EndContainer>
          <TimeButton value={entry?.timeslot} onChange={onChangeTimeslot} />
          <IconButton
            icon={X}
            onClick={_onDelete}
            color={Colors.Default}
            size={Sizes.Small}
          />
        </Styled.EndContainer>
      )}
    </Styled.WeekDayContainer>
  );
};
